import { AiEnabledGuard, AiNotEnabledFallback, FeatureFlagGuard, PlanGuard } from '@/components/guards';
import type { ReactNode } from 'react';
import NotFoundPage from '@/pages/not-found.page';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { generatePath } from '@/services/router/generate-path';
import type { Permission } from '@customerio/hydra-permissions';
import {
  AIBusinessProfile,
  AccountApiKeysPage,
  AiSettingsPage,
  AssetsPage,
  AuditLogsPage,
  DeliverabilityPage,
  ExperimentalFeaturesPage,
  ExportsPage,
  HarSanitizerPage,
  NoAccessibleWorkspacesPage,
  PostCancelPage,
  PrivacySettingsPage,
  SnippetsLayout,
  WorkspaceApiKeysPage,
  WorkspaceAutoArchivePage,
  WorkspaceAuditLogsPage,
  WorkspaceMergeOptionsPage,
  WorkspaceSinchSettingsPage,
  WorkspaceSuppressionsPage,
} from './-route-pages';
import { LastWorkspaceResolver, LazyPage } from './-route-layouts';

export function AccountApiKeysRoute() {
  return (
    <ProtectedRoute permission="account/api_credentials/view" redirectTo="/settings">
      <LazyPage>
        <AccountApiKeysPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function AccountAuditLogsRoute() {
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <AuditLogsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function ExperimentalFeaturesRoute() {
  return (
    <LazyPage>
      <ExperimentalFeaturesPage />
    </LazyPage>
  );
}

export function AccountAiRoute() {
  return (
    <LazyPage>
      <AiSettingsPage />
    </LazyPage>
  );
}

export function AccountPrivacyRoute() {
  return (
    <LazyPage>
      <PrivacySettingsPage />
    </LazyPage>
  );
}

export function WorkspaceAiBusinessProfileRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <AIBusinessProfile />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceAuditLogsRoute() {
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/view"
        redirectTo={(params) =>
          generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
        }
        allowReadOnlyMasqAccess={true}
      >
        <LazyPage>
          <WorkspaceAuditLogsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceApiKeysRoute() {
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/api_credentials/view"
        redirectTo={(params) =>
          generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
        }
      >
        <LazyPage>
          <WorkspaceApiKeysPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceAutoArchiveRoute() {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.SegmentAutoArchive} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <ProtectedRoute
          permission="journeys/workspace_settings/view"
          redirectTo={(params) =>
            generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
          }
        >
          <LazyPage>
            <WorkspaceAutoArchivePage />
          </LazyPage>
        </ProtectedRoute>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function ExportsRoute() {
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/exports/view"
        redirectTo={(params) => generatePath('/workspaces/:workspaceId/home', { workspaceId: params.workspaceId ?? 0 })}
      >
        <LazyPage>
          <ExportsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceMergeOptionsRoute() {
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        redirectTo={(params) =>
          generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
        }
      >
        <LazyPage>
          <WorkspaceMergeOptionsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceSuppressionsRoute() {
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        redirectTo={(params) =>
          generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
        }
      >
        <LazyPage>
          <WorkspaceSuppressionsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceSinchSettingsRoute() {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.CustomSmsSinch} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <ProtectedRoute
          permission="journeys/workspace_settings/view"
          redirectTo={(params) =>
            generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
          }
        >
          <LazyPage>
            <WorkspaceSinchSettingsPage />
          </LazyPage>
        </ProtectedRoute>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function HarSanitizerRoute() {
  return (
    <LazyPage>
      <HarSanitizerPage />
    </LazyPage>
  );
}

export function NoAccessibleWorkspacesRoute() {
  return (
    <LazyPage>
      <NoAccessibleWorkspacesPage />
    </LazyPage>
  );
}

export function PostCancelRoute() {
  return (
    <LazyPage>
      <PostCancelPage />
    </LazyPage>
  );
}

export function DeliverabilityRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <DeliverabilityPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

type ChildRouteProps = {
  children: ReactNode;
};

type GeofencesRouteProps = ChildRouteProps & {
  permission: Permission;
  redirectTo: (params: Record<string, string | undefined>) => string;
};

export function AiRoute({ children }: ChildRouteProps) {
  return (
    <AiEnabledGuard fallback={AiNotEnabledFallback}>
      <LastWorkspaceResolver>{children}</LastWorkspaceResolver>
    </AiEnabledGuard>
  );
}

export function AiSkillsRoute({ children }: ChildRouteProps) {
  return (
    <AiEnabledGuard fallback={AiNotEnabledFallback}>
      <LastWorkspaceResolver>{children}</LastWorkspaceResolver>
    </AiEnabledGuard>
  );
}

export function GeofencesRoute({ children, permission, redirectTo }: GeofencesRouteProps) {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.GeoFencingEnabled} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <ProtectedRoute permission={permission} redirectTo={redirectTo}>
          <LazyPage>{children}</LazyPage>
        </ProtectedRoute>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function AssetsRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <AssetsPage />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

export function SnippetsLayoutRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <SnippetsLayout />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}
